import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import CircleService from "../../services/circle.service";

// Common error handler
const getErrorMessage = (error) => {
  return (
    error.response?.data?.message ||
    error.message ||
    "Something went wrong"
  );
};

// GET ALL CIRCLES
export const getCircles = createAsyncThunk(
  "circle/getCircles",
  async (params = {}, thunkAPI) => {
    try {
      const response = await CircleService.getCircles(params);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch circles"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// GET CIRCLE BY ID
export const getCircleById = createAsyncThunk(
  "circle/getCircleById",
  async (id, thunkAPI) => {
    try {
      const response = await CircleService.getCircleById(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch circle details"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// CREATE CIRCLE
export const createCircle = createAsyncThunk(
  "circle/createCircle",
  async (payload, thunkAPI) => {
    try {
      const response = await CircleService.createCircle(payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to create circle"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// BULK CREATE CIRCLES
export const bulkCreateCircle = createAsyncThunk(
  "circle/bulkCreateCircle",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await CircleService.bulkCreateCircle(payload);

      if (response?.status !== 1) {
        return rejectWithValue(
          response?.message || "Failed to bulk create circles"
        );
      }

      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// UPDATE CIRCLE
export const updateCircle = createAsyncThunk(
  "circle/updateCircle",
  async ({ id, payload }, thunkAPI) => {
    try {
      const response = await CircleService.updateCircle(id, payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to update circle"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// DELETE CIRCLE
export const deleteCircle = createAsyncThunk(
  "circle/deleteCircle",
  async (id, thunkAPI) => {
    try {
      const response = await CircleService.deleteCircle(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to delete circle"
        );
      }

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  circles: [],
  selectedCircle: null,

  loading: false,
  detailsLoading: false,
  createLoading: false,
  bulkCreateLoading: false,
  updateLoading: false,
  deleteLoading: false,

  bulkCreateResult: null,

  error: null,
  detailsError: null,
  successMessage: null,

  pagination: {
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  },
};

const circleSlice = createSlice({
  name: "circle",
  initialState,

  reducers: {
    clearCircleError: (state) => {
      state.error = null;
      state.detailsError = null;
    },

    clearCircleMessage: (state) => {
      state.successMessage = null;
    },

    clearSelectedCircle: (state) => {
      state.selectedCircle = null;
    },

    clearBulkCircleResult: (state) => {
      state.bulkCreateResult = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // GET ALL CIRCLES
      .addCase(getCircles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getCircles.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || {};

        state.circles = Array.isArray(data)
          ? data
          : data.circles || [];

        state.pagination = {
          total: Array.isArray(data)
            ? data.length
            : data.total ?? state.circles.length,

          currentPage: data.currentPage ?? 1,
          totalPages: data.totalPages ?? 1,
          limit: data.limit ?? state.circles.length,
        };
      })

      .addCase(getCircles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // GET CIRCLE BY ID
      .addCase(getCircleById.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })

      .addCase(getCircleById.fulfilled, (state, action) => {
        state.detailsLoading = false;

        const data = action.payload || {};
        state.selectedCircle =
          data.circle || data.Circle || data;
      })

      .addCase(getCircleById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload || action.error.message;
      })

      // CREATE CIRCLE
      .addCase(createCircle.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(createCircle.fulfilled, (state, action) => {
        state.createLoading = false;

        state.successMessage =
          action.payload?.message || "Circle created successfully";

        const data = action.payload?.data;
        const circle = data?.circle || data;

        if (circle?._id) {
          state.circles.unshift(circle);
          state.pagination.total += 1;
        }
      })

      .addCase(createCircle.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload || action.error.message;
      })

      // BULK CREATE CIRCLES
      .addCase(bulkCreateCircle.pending, (state) => {
        state.bulkCreateLoading = true;
        state.error = null;
        state.successMessage = null;
        state.bulkCreateResult = null;
      })

      .addCase(bulkCreateCircle.fulfilled, (state, action) => {
        state.bulkCreateLoading = false;

        state.bulkCreateResult = action.payload?.data || null;

        state.successMessage =
          action.payload?.message || "Circles imported successfully";
      })

      .addCase(bulkCreateCircle.rejected, (state, action) => {
        state.bulkCreateLoading = false;
        state.error = action.payload || action.error.message;
      })

      // UPDATE CIRCLE
      .addCase(updateCircle.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(updateCircle.fulfilled, (state, action) => {
        state.updateLoading = false;

        state.successMessage =
          action.payload?.message || "Circle updated successfully";

        const data = action.payload?.data;
        const updatedCircle = data?.circle || data;

        if (updatedCircle?._id) {
          const index = state.circles.findIndex(
            (circle) => circle._id === updatedCircle._id
          );

          if (index !== -1) {
            state.circles[index] = updatedCircle;
          }

          if (state.selectedCircle?._id === updatedCircle._id) {
            state.selectedCircle = updatedCircle;
          }
        }
      })

      .addCase(updateCircle.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || action.error.message;
      })

      // DELETE CIRCLE
      .addCase(deleteCircle.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(deleteCircle.fulfilled, (state, action) => {
        state.deleteLoading = false;

        state.circles = state.circles.filter(
          (circle) => circle._id !== action.payload
        );

        if (state.selectedCircle?._id === action.payload) {
          state.selectedCircle = null;
        }

        state.pagination.total = Math.max(
          0,
          state.pagination.total - 1
        );

        state.successMessage = "Circle deleted successfully";
      })

      .addCase(deleteCircle.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const {
  clearCircleError,
  clearCircleMessage,
  clearSelectedCircle,
  clearBulkCircleResult,
} = circleSlice.actions;

export default circleSlice.reducer;